import { useCallback, useEffect, useRef, type ClipboardEvent, type KeyboardEvent } from 'react';

/** Characters a PIN may hold. The server's alphabet, written once on this side too. */
const ALLOWED = /^[0-9A-Z]$/;

const PIN_LENGTH = 6;

/**
 * Six boxes, one character each.
 *
 * **The value is ONE string and the boxes are a rendering of it.** Six pieces of state is six
 * things that can disagree, and the thing actually submitted is a string — so it is held as
 * one, and each box reads its own character out of it. That is what makes paste, backspace
 * across a boundary and a short value all fall out rather than needing their own handling.
 *
 * **It is not a text field wearing six borders.** What a reader does here is different from
 * typing into an input: there is no caret to place, no selection, and the only editing verbs
 * are "add one" and "take one away". The keyboard contract below is the whole of it.
 *
 * Uppercase is applied on the way IN, because the server compares normalised values and a
 * reader holding shift must not be told their PIN is wrong on six boxes showing one character
 * each, with nothing on screen to explain the difference.
 */
export function PinBoxes({
  id,
  value,
  onChange,
  onComplete,
  label,
  describedBy,
  disabled = false,
  invalid = false,
  autoFocus = false,
  autoComplete = 'one-time-code',
}: {
  id: string;
  value: string;
  onChange: (next: string) => void;
  /** Fired when the last box is filled, so the form can submit without a second gesture. */
  onComplete?: (value: string) => void;
  /** The accessible name for the group. Required — six boxes announce nothing on their own. */
  label: string;
  describedBy?: string;
  disabled?: boolean;
  invalid?: boolean;
  autoFocus?: boolean;
  autoComplete?: string;
}) {
  const boxes = useRef<(HTMLInputElement | null)[]>([]);

  const focusBox = useCallback((index: number) => {
    const target = boxes.current[Math.min(Math.max(index, 0), PIN_LENGTH - 1)];
    target?.focus();
    target?.select();
  }, []);

  // A value cleared from outside — a refused PIN, a reset form — puts the caret back at the
  // start, so the reader types the next attempt where they are already looking.
  useEffect(() => {
    if (value.length === 0 && document.activeElement instanceof HTMLInputElement
      && boxes.current.includes(document.activeElement)) {
      focusBox(0);
    }
  }, [value, focusBox]);

  /*
    Writing is GAPLESS by construction.

    Holding the value as one string means box N is character N, and a hole in the middle has no
    representation — `['A', '', '', 'D'].join('')` is `"AD"`, so a character typed into the
    fourth box with the second empty would appear in the second. Rather than invent a filler
    character that then has to be stripped on every read, a write past the end of the value is
    CLAMPED to the end: arrowing to box 4 of an empty control and typing fills box 1, which is
    also where the caret visibly is.
  */
  function write(index: number, character: string) {
    const at = Math.min(index, value.length);
    const next = (value.slice(0, at) + character + value.slice(at + 1)).slice(0, PIN_LENGTH);

    onChange(next);

    if (next.length === PIN_LENGTH) {
      onComplete?.(next);
      return;
    }

    focusBox(at + 1);
  }

  function handleKeyDown(index: number, event: KeyboardEvent<HTMLInputElement>) {
    /*
      BACKSPACE ON AN EMPTY BOX STEPS BACK AND CLEARS THE ONE BEFORE IT.

      Without it a reader who mistyped the fourth character presses backspace, watches nothing
      happen, and has to find the previous box with the mouse. It is the single most-used key
      on a control like this and the one that is most often missed, because the box the reader
      is standing in is already empty and the handler has nothing local to do.
    */
    if (event.key === 'Backspace') {
      event.preventDefault();

      if (value[index]) {
        onChange(value.slice(0, index) + value.slice(index + 1));
        return;
      }

      onChange(value.slice(0, index - 1) + value.slice(index));
      focusBox(index - 1);
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      focusBox(index - 1);
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      focusBox(index + 1);
      return;
    }

    if (event.key === 'Delete') {
      event.preventDefault();
      onChange(value.slice(0, index) + value.slice(index + 1));
      return;
    }

    // Everything else that is one character long is a candidate. Modifier combinations keep
    // their browser meaning — Ctrl+V must still reach the paste handler.
    if (event.key.length !== 1 || event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }

    event.preventDefault();

    const character = event.key.toUpperCase();

    if (ALLOWED.test(character)) {
      write(index, character);
    }
  }

  /*
    PASTE FILLS THE WHOLE CONTROL, from whichever box received it.

    A PIN arrives from a password manager or a note as one string, and six boxes that each took
    one character of it would leave five on the floor. Anything outside the alphabet is dropped
    rather than refused — a value copied with a surrounding space or a dash is still the PIN the
    reader meant.
  */
  function handlePaste(index: number, event: ClipboardEvent<HTMLInputElement>) {
    event.preventDefault();

    const pasted = event.clipboardData
      .getData('text')
      .toUpperCase()
      .split('')
      .filter((c) => ALLOWED.test(c))
      .join('');

    if (pasted.length === 0) {
      return;
    }

    const next = (value.slice(0, index) + pasted).slice(0, PIN_LENGTH);

    onChange(next);

    if (next.length === PIN_LENGTH) {
      onComplete?.(next);
      return;
    }

    focusBox(next.length);
  }

  return (
    <div
      role="group"
      aria-label={label}
      // It renders no error text of its own, so there is no deferral to do here and no id to
      // invent: the CALLER owns the message and points at it. An `aria-describedby` naming an
      // element nobody renders announces nothing and reads as wired.
      aria-describedby={describedBy}
      className="flex items-center justify-center gap-2"
    >
      {Array.from({ length: PIN_LENGTH }, (_, index) => (
        <input
          key={index}
          ref={(element) => { boxes.current[index] = element; }}
          id={index === 0 ? id : `${id}-${index}`}
          // One character, so a phone offers the right keyboard and nothing tries to autocorrect
          // it into a word.
          inputMode="text"
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          // Only the first box carries it: six one-time-code fields make a password manager
          // offer the same value six times.
          autoComplete={index === 0 ? autoComplete : 'off'}
          autoFocus={autoFocus && index === 0}
          maxLength={1}
          disabled={disabled}
          value={value[index] ?? ''}
          aria-label={`${label}, character ${index + 1} of ${PIN_LENGTH}`}
          aria-invalid={invalid || undefined}
          // Every keystroke goes through onKeyDown, which owns the whole contract. onChange
          // exists so React does not warn about a controlled input with no handler, and so a
          // phone's own composition still lands.
          onChange={(event) => {
            const character = event.target.value.slice(-1).toUpperCase();
            if (ALLOWED.test(character)) {
              write(index, character);
            }
          }}
          onKeyDown={(event) => handleKeyDown(index, event)}
          onPaste={(event) => handlePaste(index, event)}
          onFocus={(event) => event.target.select()}
          className={[
            'h-12 w-10 rounded-lg border bg-white text-center font-mono text-lg font-medium tabular-nums',
            'text-slate-800 shadow-sm transition',
            'focus:outline-none focus-visible:ring-2',
            disabled ? 'cursor-not-allowed bg-slate-50 text-slate-400' : '',
            invalid
              ? 'border-red-300 focus-visible:border-red-400 focus-visible:ring-red-100'
              : 'border-slate-300 focus-visible:border-brand-400 focus-visible:ring-brand-100',
          ].filter(Boolean).join(' ')}
        />
      ))}
    </div>
  );
}

export { PIN_LENGTH };

/**
 * A suggested PIN, for the enrolment form's generator.
 *
 * **Generated in the BROWSER, from `crypto.getRandomValues`.** The server could mint one, but
 * then a credential the account holder is supposed to be the only holder of would have crossed
 * the network before they ever typed it, and would sit in a response log. Generating it here
 * means the only time it is transmitted is when the reader submits it, like any PIN they chose
 * themselves.
 *
 * The rejection loop is what keeps it UNIFORM: taking a random byte modulo 36 would make the
 * first four characters of the alphabet very slightly likelier than the rest. The bias is tiny
 * and the fix is three lines, so there is no reason to carry it.
 */
export function suggestPin(): string {
  const alphabet = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const limit = Math.floor(256 / alphabet.length) * alphabet.length;
  const out: string[] = [];

  while (out.length < PIN_LENGTH) {
    const bytes = crypto.getRandomValues(new Uint8Array(PIN_LENGTH));

    for (const byte of bytes) {
      if (byte < limit && out.length < PIN_LENGTH) {
        out.push(alphabet[byte % alphabet.length]);
      }
    }
  }

  return out.join('');
}
